import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import { useRemediationWorkflows } from "../contexts/FeatureFlagsContext";
import Navigation from "./Navigation";
import Card from "./Card";
import Button from "./Buttons";
import LoadingSpinner from "./LoadingSpinner";
import OrgAssignAssessModal from "./OrgAssignAssessModal";
import RiskAcceptanceModal from "./RiskAcceptanceModal";
import AssignRiskModal from "./AssignRiskModal";
import RiskApprovalModal from "./RiskApprovalModal";
import { FileText, Shield, Building2, Globe, Brain, Handshake, AlertTriangle, Laptop, Briefcase, CheckCircle, UserPlus, ClipboardList, ClipboardCheck, Eye, Clock, Scan, Scale, ShieldOff, BookOpen, Workflow } from "lucide-react";

// Assessment type configuration
const ASSESSMENT_TYPES = {
  standard: {
    label: "Standard",
    color: "bg-indigo-100 text-indigo-800 border-indigo-300",
    endpoint: "/assessments?includeAll=true",
    idField: "assessmentID",
    assignedField: "assessmentAssignedToUserId",
    statusField: "status",
    progressField: "progress",
    risksField: "risks",
    assessRoute: "/assessment",
    risksRoute: (id) => `/assessment/${id}/risks`,
    icon: Laptop,
  },
  businessUnit: {
    label: "Business Unit",
    color: "bg-cyan-100 text-cyan-800 border-cyan-300",
    endpoint: "/businessUnitAssessments",
    idField: "businessUnitAssessmentId",
    assignedField: "assessmentAssignedToUserId",
    statusField: "businessUnitStatus",
    progressField: "progress",
    risksField: "risks",
    assessRoute: "/businessunitassessment",
    risksRoute: (id) => `/businessunitassessment/${id}/risks`,
    icon: Briefcase,
  },
  enterprise: {
    label: "Enterprise",
    color: "bg-blue-100 text-blue-800 border-blue-300",
    endpoint: "/enterpriseAssessments?includeAll=true",
    idField: "enterpriseAssessmentID",
    assignedField: "enterpriseAssignedToUserId",
    statusField: "enterpriseStatus",
    progressField: "enterpriseProgress",
    risksField: "enterpriseRisks",
    assessRoute: "/EnterpriseAssessment",
    risksRoute: (id) => `/EnterpriseAssessments/${id}/risks`,
    icon: Building2,
  },
  ransomware: {
    label: "Ransomware",
    color: "bg-red-100 text-red-800 border-red-300",
    endpoint: "/ransomwareAssessments?includeAll=true",
    idField: "ransomwareAssessmentID",
    assignedField: "ransomwareAssignedToUserId",
    statusField: "ransomwareStatus",
    progressField: "ransomwareProgress",
    risksField: "ransomwareRisks",
    assessRoute: "/RansomwareAssessment",
    risksRoute: (id) => `/RansomwareAssessment/${id}/risks`,
    icon: Shield,
  },
  darkweb: {
    label: "Dark Web",
    color: "bg-purple-100 text-purple-800 border-purple-300",
    endpoint: "/darkWebAssessments?includeAll=true",
    idField: "darkWebAssessmentID",
    assignedField: "darkWebAssignedToUserId",
    statusField: "darkWebStatus",
    progressField: "darkWebProgress",
    risksField: "darkWebRisks",
    assessRoute: "/DarkWebAssessment",
    risksRoute: (id) => `/DarkWebAssessment/${id}/risks`,
    icon: Globe,
  },
  ai: {
    label: "AI",
    color: "bg-green-100 text-green-800 border-green-300",
    endpoint: "/aiAssessments?includeAll=true",
    idField: "aiAssessmentID",
    assignedField: "aiAssignedToUserId",
    statusField: "aiStatus",
    progressField: "aiProgress",
    risksField: "aiRisks",
    assessRoute: "/AIAssessment",
    risksRoute: (id) => `/AIAssessment/${id}/risks`,
    icon: Brain,
  },
  tprm: {
    label: "TPRM",
    color: "bg-orange-100 text-orange-800 border-orange-300",
    endpoint: "/TPRMAssessments?includeAll=true",
    idField: "tprmAssessmentID",
    assignedField: "tprmAssignedToUserId",
    statusField: "tprmStatus",
    progressField: "tprmProgress",
    risksField: "tprmRisks",
    assessRoute: "/TPRMAssessment",
    risksRoute: (id, assessment) => `/VendorDashboard/${assessment?.vendorId}`,
    icon: Handshake,
  },
};

// Risk type configuration for fetching from risk endpoints
const RISK_TYPES = {
  standard: {
    label: "Standard",
    color: "bg-indigo-100 text-indigo-800 border-indigo-300",
    detailsEndpoint: "/risks/details",
    assignEndpoint: (id) => `/risks/${id}/assign`,
    icon: Laptop,
  },
  businessUnit: {
    label: "Business Unit",
    color: "bg-cyan-100 text-cyan-800 border-cyan-300",
    detailsEndpoint: "/businessunitrisks/details",
    assignEndpoint: (id) => `/businessunitrisks/${id}/assign`,
    icon: Briefcase,
  },
  enterprise: {
    label: "Enterprise",
    color: "bg-blue-100 text-blue-800 border-blue-300",
    detailsEndpoint: "/enterpriserisks/details",
    assignEndpoint: (id) => `/enterpriserisks/${id}/assign`,
    icon: Building2,
  },
  ransomware: {
    label: "Ransomware",
    color: "bg-red-100 text-red-800 border-red-300",
    detailsEndpoint: "/ransomwarerisks/details",
    assignEndpoint: (id) => `/ransomwarerisks/${id}/assign`,
    icon: Shield,
  },
  darkweb: {
    label: "Dark Web",
    color: "bg-purple-100 text-purple-800 border-purple-300",
    detailsEndpoint: "/darkwebrisks/details",
    assignEndpoint: (id) => `/darkwebrisks/${id}/assign`,
    icon: Globe,
  },
  ai: {
    label: "AI",
    color: "bg-green-100 text-green-800 border-green-300",
    detailsEndpoint: "/airisks/details",
    assignEndpoint: (id) => `/airisks/${id}/assign`,
    icon: Brain,
  },
  tprm: {
    label: "TPRM",
    color: "bg-orange-100 text-orange-800 border-orange-300",
    detailsEndpoint: "/tprmrisk/details",
    assignEndpoint: (id) => `/tprmrisk/${id}/assign`,
    icon: Handshake,
  },
  grc: {
    label: "GRC",
    color: "bg-rose-100 text-rose-800 border-rose-300",
    detailsEndpoint: "/grcrisks/details",
    assignEndpoint: (id) => `/grcrisks/${id}/assign`,
    approveBaseEndpoint: (id) => `/grcrisks/${id}`,
    icon: Scale,
  },
  external: {
    label: "External",
    color: "bg-cyan-100 text-cyan-800 border-cyan-300",
    detailsEndpoint: "/unifiedrisks/details",
    assignEndpoint: (id) => `/unifiedrisks/${id}/assign`,
    approveBaseEndpoint: (id) => `/unifiedrisks/${id}`,
    icon: Scan,
  },
};

// Risks all come from one unified endpoint (/unifiedrisks/details). The unified DTO's `riskType`
// field carries the real frontend type ("Deals", "Enterprise", "DarkWeb", "Ransomware", "AI",
// "BusinessUnits", "TPRM", "GRC", or "External"); map it to the matching RISK_TYPES key so each
// risk shows its true label/icon/color. Actions still route through the unified controller.
const DTO_RISK_TYPE_TO_KEY = {
  Deals: "standard",
  Enterprise: "enterprise",
  DarkWeb: "darkweb",
  Ransomware: "ransomware",
  AI: "ai",
  BusinessUnits: "businessUnit",
  TPRM: "tprm",
  GRC: "grc",
  External: "external",
};

// GRC evidence request statuses (backend EvidenceRequestStatus). A request is "done"
// only while Satisfied — Pending/Overdue/Expired all mean evidence is owed.
const EVIDENCE_STATUS_STYLES = {
  Pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  Overdue: "bg-red-100 text-red-800 border-red-300",
  Expired: "bg-red-100 text-red-800 border-red-300",
  Satisfied: "bg-green-100 text-green-700 border-green-300",
};

// Vendor performance scorecard statuses (backend VendorPerformanceScorecardStatus).
// Response-action labels, matching AiResponseActionsPanel's ACTION_LABEL.
const RESPONSE_ACTION_LABEL = {
  DisableEntraIdentity: "Disable Entra identity",
  QuarantineAwsRole: "Quarantine AWS role",
  DisableAnthropicWorkspaceKeys: "Deactivate Anthropic workspace keys",
  DeleteOpenAiProjectKeys: "Delete OpenAI project keys",
  DisableGcpServiceAccount: "Disable Google Cloud service account",
};

const SCORECARD_STATUS_STYLES = {
  Pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  Overdue: "bg-red-100 text-red-800 border-red-300",
  Completed: "bg-green-100 text-green-700 border-green-300",
};

const MyTasks = () => {
  const [assessmentsAssignedToMe, setAssessmentsAssignedToMe] = useState([]);
  const [assessmentsRequiringAssignment, setAssessmentsRequiringAssignment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [risksAssignedToMe, setRisksAssignedToMe] = useState([]);
  const [risksRequiringAssignment, setRisksRequiringAssignment] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [risksLoading, setRisksLoading] = useState(true);
  const [evidenceRequests, setEvidenceRequests] = useState([]);
  const [policyTasks, setPolicyTasks] = useState({ pendingReviews: [], pendingAttestations: [] });
  const [policyTasksLoading, setPolicyTasksLoading] = useState(true);
  const [evidenceLoading, setEvidenceLoading] = useState(true);
  const [scorecards, setScorecards] = useState([]);
  const [scorecardsLoading, setScorecardsLoading] = useState(true);
  const [responseApprovals, setResponseApprovals] = useState([]);
  const [responseApprovalsLoading, setResponseApprovalsLoading] = useState(true);
  const [playbookTasks, setPlaybookTasks] = useState([]);
  const [playbookTasksLoading, setPlaybookTasksLoading] = useState(true);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [workflowStepsLoading, setWorkflowStepsLoading] = useState(true);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showAssignRiskModal, setShowAssignRiskModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [activeSection, setActiveSection] = useState("assessments-assigned");

  const navigate = useNavigate();
  const apiCall = useApi();
  const { user } = useAuth();
  const { canManageOrganizationRisks } = useRBAC();
  // Features:RemediationWorkflows (UI-only, task 15): off hides the Workflow steps section and
  // skips its fetch entirely.
  const remediationWorkflows = useRemediationWorkflows();

  const selectedOrg = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canManageRisks = canManageOrganizationRisks();

  // Completion predicates — used to split "Assigned to Me" into Pending and
  // Completed buckets. Risk completion is OR, not AND: Accepted OR Remediated.
  const isAssessmentCompleted = (a) => {
    const s = a[a._config.statusField];
    return s === "Completed" || s === "completed";
  };
  const isRiskCompleted = (r) => {
    const accepted = (r.status || "").toLowerCase() === "accepted";
    const remediated = r.isRemediationCompleted === true;
    return accepted || remediated;
  };

  // Fetch all assessments from all types
  const fetchAllAssessments = async () => {
    setLoading(true);
    setError(null);

    try {
      const allAssessments = [];

      // Fetch assessments from each type in parallel
      const fetchPromises = Object.entries(ASSESSMENT_TYPES).map(async ([type, config]) => {
        try {
          const data = await apiCall("GET", config.endpoint);
          if (Array.isArray(data)) {
            return data.map((assessment) => ({
              ...assessment,
              _type: type,
              _config: config,
            }));
          }
          return [];
        } catch (err) {
          return [];
        }
      });

      const results = await Promise.all(fetchPromises);
      results.forEach((assessments) => allAssessments.push(...assessments));

      // Filter assessments
      const currentUserId = user?.id;

      // Assessments assigned to me (including completed).
      // Assigned = legacy single owner OR a respondent row (assigneeUserIds — TPRM only for now).
      const isAssignedToMe = (a) => {
        const assignedUserId = a[a._config.assignedField];
        // Compare as strings to handle potential type mismatches (int vs string)
        if (String(assignedUserId) === String(currentUserId)) return true;
        return (a.assigneeUserIds || []).some(
          (id) => String(id) === String(currentUserId)
        );
      };
      const assignedToMe = allAssessments.filter(isAssignedToMe);

      // Assessments requiring assignment (no owner and no respondents, not completed)
      const requiresAssignment = allAssessments.filter((a) => {
        const assignedUserId = a[a._config.assignedField];
        const status = a[a._config.statusField];
        const isCompleted = status === "completed" || status === "Completed";
        const hasRespondents = (a.assigneeUserIds || []).length > 0;
        return !assignedUserId && !hasRespondents && !isCompleted;
      });

      setAssessmentsAssignedToMe(assignedToMe);
      setAssessmentsRequiringAssignment(requiresAssignment);
    } catch (err) {
      setError("Failed to load assessments. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all risks from all risk type endpoints
  const fetchAllRisks = async () => {
    setRisksLoading(true);
    try {
      const allRisks = [];
      // All risks live in one table — fetch /unifiedrisks/details once. Use each risk's real
      // `riskType` for the displayed label/icon/color, but keep every action (assign/approve/details)
      // pointed at the unified controller via the 'external' config's endpoints.
      try {
        const data = await apiCall("GET", RISK_TYPES.external.detailsEndpoint);
        if (Array.isArray(data)) {
          data.forEach((risk) => {
            const typeKey = DTO_RISK_TYPE_TO_KEY[risk.riskType] || "external";
            const display = RISK_TYPES[typeKey] || RISK_TYPES.external;
            allRisks.push({
              ...risk,
              _riskType: typeKey,
              _riskConfig: {
                ...RISK_TYPES.external,
                label: display.label,
                color: display.color,
                icon: display.icon,
              },
            });
          });
        }
      } catch (err) {}

      const currentUserId = user?.id;

      // All risks assigned to me (any status). Pending vs Completed split is
      // derived below via isRiskCompleted — keeps the filter logic in one place.
      const assignedToMe = allRisks.filter((r) =>
        r.assignedToUserId && String(r.assignedToUserId) === String(currentUserId)
      );

      // Risks with no assignee that aren't already done. A risk counts as
      // "done" if it's Accepted OR its remediation is Completed — the OR
      // predicate is load-bearing (see STE-304).
      const needsAssignment = allRisks.filter((r) => {
        const isAccepted = (r.status || "").toLowerCase() === "accepted";
        const isRemediated = r.isRemediationCompleted === true;
        return !r.assignedToUserId && !isAccepted && !isRemediated;
      });

      // Risks pending my approval (primary or secondary)
      const pending = allRisks.filter((r) =>
        (r.primaryApprovalStatus === 1 && String(r.primaryApproverUserId) === String(currentUserId)) ||
        (r.secondaryApprovalStatus === 1 && String(r.secondaryApproverUserId) === String(currentUserId))
      );

      setRisksAssignedToMe(assignedToMe);
      setRisksRequiringAssignment(needsAssignment);
      setPendingApprovals(pending);
    } catch (err) {} finally {
      setRisksLoading(false);
    }
  };

  // Fetch GRC evidence requests assigned to me. 404 (GRC readiness feature off) or missing
  // permissions simply mean no evidence tasks — never an error state.
  const fetchEvidenceRequests = async () => {
    setEvidenceLoading(true);
    try {
      const data = await apiCall("GET", "/security-insights/evidence/requests/my");
      setEvidenceRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      setEvidenceRequests([]);
    } finally {
      setEvidenceLoading(false);
    }
  };

  // Policy reviews and attestation assignments waiting on me (policy lifecycle). Any failure or a
  // pre-rollout backend simply means no policy tasks — never an error state.
  const fetchPolicyTasks = async () => {
    setPolicyTasksLoading(true);
    try {
      const data = await apiCall("GET", "/security-insights/policy-docs/my");
      setPolicyTasks({
        pendingReviews: Array.isArray(data?.pendingReviews) ? data.pendingReviews : [],
        pendingAttestations: Array.isArray(data?.pendingAttestations) ? data.pendingAttestations : [],
      });
    } catch (err) {
      setPolicyTasks({ pendingReviews: [], pendingAttestations: [] });
    } finally {
      setPolicyTasksLoading(false);
    }
  };

  // Fetch vendor performance scorecards assigned to me (as reviewer). Missing permissions or
  // a pre-rollout schema simply mean no review tasks — never an error state.
  const fetchScorecards = async () => {
    setScorecardsLoading(true);
    try {
      const data = await apiCall("GET", "/tprm-performance-scorecards/my");
      setScorecards(Array.isArray(data) ? data : []);
    } catch (err) {
      setScorecards([]);
    } finally {
      setScorecardsLoading(false);
    }
  };

  // Fetch AI response actions waiting on me as the assigned approver. Feature off or missing
  // permissions simply mean no approval tasks — never an error state.
  const fetchResponseApprovals = async () => {
    setResponseApprovalsLoading(true);
    try {
      const data = await apiCall("GET", "/ai-assets/response-actions/my-approvals");
      setResponseApprovals(Array.isArray(data) ? data : []);
    } catch (err) {
      setResponseApprovals([]);
    } finally {
      setResponseApprovalsLoading(false);
    }
  };

  // Fetch open playbook-fill assignments (§11h phase 2). Feature off or missing permissions
  // simply mean no playbook tasks — never an error state.
  const fetchPlaybookTasks = async () => {
    setPlaybookTasksLoading(true);
    try {
      const data = await apiCall("GET", "/ai-assets/playbook-assignments/my");
      setPlaybookTasks(Array.isArray(data) ? data : []);
    } catch (err) {
      setPlaybookTasks([]);
    } finally {
      setPlaybookTasksLoading(false);
    }
  };

  // Fetch the workflow steps currently assigned to me (custom workflows, task 16). The endpoint
  // is scoped to the session user; any failure simply means no workflow tasks — never an error state.
  const fetchWorkflowSteps = async () => {
    setWorkflowStepsLoading(true);
    try {
      const data = await apiCall("GET", "/remediation-workflows/my-steps");
      setWorkflowSteps(Array.isArray(data) ? data : []);
    } catch (err) {
      setWorkflowSteps([]);
    } finally {
      setWorkflowStepsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllAssessments();
    fetchAllRisks();
    fetchEvidenceRequests();
    fetchScorecards();
    fetchResponseApprovals();
    fetchPlaybookTasks();
    fetchPolicyTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The flag resolves after the first render (flags load async), so this fetch keys on it.
  useEffect(() => {
    if (remediationWorkflows) fetchWorkflowSteps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remediationWorkflows]);

  // Build structured assessment title per STE-173:
  // Deal: "Deal <DealName> [<Type>] Assessment"
  // BU: "Business Unit <BUName> [<Type>] Assessment"
  // Vendor: "<VendorName> <Type> Assessment"
  // Org: "<Type> Organization Assessment"
  const getAssessmentTitle = (assessment) => {
    const dealName = assessment.dealName || assessment.DealName;
    const buName = assessment.businessUnitName || assessment.BusinessUnitName;
    const vendorName = assessment.vendorName || assessment.VendorName;
    const type = assessment._type;

    // Type label is empty for standard/businessUnit (regular assessments)
    const typeLabel = (type === "standard" || type === "businessUnit") ? "" : ASSESSMENT_TYPES[type].label;
    const typePart = typeLabel ? `${typeLabel} ` : "";

    if (dealName) {
      return `Deal ${dealName} ${typePart}Assessment`;
    }
    if (buName) {
      return `Business Unit ${buName} ${typePart}Assessment`;
    }
    if (vendorName) {
      return `${vendorName} ${typePart}Assessment`;
    }
    // Organization-level assessment
    if (typeLabel) {
      return `${typeLabel} Organization Assessment`;
    }
    return `Organization Assessment`;
  };

  // Handle assess button click
  const handleAssess = (assessment) => {
    const config = assessment._config;
    const assessmentId = assessment[config.idField];
    const path = assessmentId
      ? `${config.assessRoute}/${assessmentId}`
      : config.assessRoute;
    navigate(path, {
      state: {
        organizationId: assessment.organizationId,
        applicationId: assessment.applicationId,
        applicationName: assessment.applicationName,
        applicationTypeId: assessment.applicationTypeId,
        dealID: assessment.dealID || assessment.dealId,
        dealName: assessment.dealName,
        verticalId: assessment.verticalId,
        regionId: assessment.regionId,
        subVerticalId: assessment.subVerticalId,
        userBaseId: assessment.userBaseId,
        deploymentModelId: assessment.deploymentModelId,
        businessUnitId: assessment.businessUnitId,
        businessUnitApplicationId: assessment.businessUnitApplicationId,
        businessUnitName: assessment.businessUnitName,
        vendorId: assessment.vendorId,
        vendorName: assessment.vendorName,
      },
    });
  };

  // Handle assign button click
  const handleAssign = (assessment) => {
    setSelectedAssessment(assessment);
    setShowAssignModal(true);
  };

  // Render a slim assessment card with title, type pill, and action button
  const renderAssessmentCard = (assessment, showAssignButton = false) => {
    const config = assessment._config;
    const TypeIcon = config.icon;
    const assessmentId = assessment[config.idField];
    const status = assessment[config.statusField];
    const isCompleted = status === "completed" || status === "Completed";
    // Due date is only populated on TPRM assessments today; mirror the scorecard chip pattern
    const isOverdue = !isCompleted && assessment.dueDate && new Date(assessment.dueDate) < new Date();
    const dueLabel = !isCompleted && assessment.dueDate
      ? new Date(assessment.dueDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
      : null;

    return (
      <div
        key={`${assessment._type}-${assessmentId}`}
        className={`p-3 border-2 rounded-lg transition-colors flex items-center justify-between ${isCompleted ? 'border-green-200 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="font-medium text-content-primary truncate">
            {getAssessmentTitle(assessment)}
          </span>
          <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 shrink-0 border ${config.color}`}>
            <TypeIcon className="h-3 w-3" />
            {config.label}
          </span>
          {isOverdue ? (
            <span className="px-2 py-1 text-xs rounded-full border shrink-0 bg-red-100 text-red-800 border-red-300">
              Overdue
            </span>
          ) : null}
          {dueLabel && (
            <span className="text-xs text-gray-500 shrink-0">Due: {dueLabel}</span>
          )}
        </div>
        <div className="ml-3 shrink-0">
          {isCompleted ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded-full">
              <CheckCircle className="h-3 w-3" />
              Completed
            </span>
          ) : showAssignButton ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => handleAssign(assessment)}
            >
              <UserPlus className="h-3 w-3 mr-1" />
              Assign
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => handleAssess(assessment)}
            >
              <FileText className="h-3 w-3 mr-1" />
              Assess
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Render a slim risk card with title, category pill, and action button
  const renderRiskCard = (risk, actionType) => {
    const config = risk._riskConfig;
    const TypeIcon = config.icon;
    const riskId = risk.id || risk.businessUnitRiskId;

    return (
      <div
        key={`${risk._riskType}-${riskId}`}
        className="p-3 border-2 rounded-lg transition-colors flex items-center justify-between border-gray-200 hover:border-gray-300"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="font-medium text-content-primary truncate">
            {risk.riskTitle || "Untitled Risk"}
          </span>
          <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 shrink-0 border ${config.color}`}>
            <TypeIcon className="h-3 w-3" />
            {config.label}
          </span>
          {risk.riskCategory && (
            <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600 border border-gray-300 shrink-0">
              {risk.riskCategory}
            </span>
          )}
        </div>
        <div className="ml-3 shrink-0">
          {actionType === "view" && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => navigate(`/risks?riskId=${riskId}`)}
              >
                <Eye className="h-3 w-3 mr-1" />
                View
              </Button>
          )}
          {actionType === "assign" && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setSelectedRisk({
                  ...risk,
                  riskId: riskId,
                  riskName: risk.riskTitle,
                  _assignEndpoint: config.assignEndpoint(riskId),
                });
                setShowAssignRiskModal(true);
              }}
            >
              <UserPlus className="h-3 w-3 mr-1" />
              Assign
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Derived: split the "assigned to me" fetch results into Pending (active)
  // and Completed buckets. Cheap filter over ~dozens of rows, no useMemo needed.
  const pendingAssessmentsAssignedToMe = assessmentsAssignedToMe.filter((a) => !isAssessmentCompleted(a));
  const completedAssessmentsAssignedToMe = assessmentsAssignedToMe.filter(isAssessmentCompleted);
  const pendingRisksAssignedToMe = risksAssignedToMe.filter((r) => !isRiskCompleted(r));
  const completedRisksAssignedToMe = risksAssignedToMe.filter(isRiskCompleted);
  const pendingEvidenceRequests = evidenceRequests.filter((r) => r.status !== "Satisfied");
  const completedEvidenceRequests = evidenceRequests.filter((r) => r.status === "Satisfied");
  const pendingScorecards = scorecards.filter((s) => s.status !== "Completed");
  const completedScorecards = scorecards.filter((s) => s.status === "Completed");

  // Sidebar groups. Each group has a heading + items; hidden items (RBAC) are
  // filtered out here so the renderer can stay dumb. `pendingAssessmentsAssignedToMe`
  // and friends are the Pending-bucket derivations — the count badges now
  // reflect actual active work, not all-assigned-including-completed.
  const sidebarGroups = [
    {
      key: "pending",
      heading: "Pending Tasks",
      items: [
        {
          id: "assessments-assigned",
          label: "Assessments Assigned to Me",
          icon: ClipboardCheck,
          count: pendingAssessmentsAssignedToMe.length,
          loading: loading,
          visible: true,
        },
        {
          id: "assessments-requiring",
          label: "Assessments Requiring Assignment",
          icon: ClipboardList,
          count: assessmentsRequiringAssignment.length,
          loading: loading,
          visible: canManageRisks,
        },
        {
          id: "risks-assigned",
          label: "Risks Assigned to Me",
          icon: AlertTriangle,
          count: pendingRisksAssignedToMe.length,
          loading: risksLoading,
          visible: true,
        },
        {
          id: "risks-requiring",
          label: "Risks Requiring Assignment",
          icon: UserPlus,
          count: risksRequiringAssignment.length,
          loading: risksLoading,
          visible: canManageRisks,
        },
        {
          id: "evidence-assigned",
          label: "Evidence Requests Assigned to Me",
          icon: FileText,
          count: pendingEvidenceRequests.length,
          loading: evidenceLoading,
          // Only shown to users who actually have evidence assignments — the GRC evidence
          // workflow is invisible to everyone else.
          visible: evidenceRequests.length > 0,
        },
        {
          id: "policy-reviews",
          label: "Policy Reviews Assigned to Me",
          icon: ClipboardCheck,
          count: policyTasks.pendingReviews.length,
          loading: policyTasksLoading,
          // Only shown to assigned reviewers with an open decision — the policy lifecycle is
          // invisible to everyone else.
          visible: policyTasks.pendingReviews.length > 0,
        },
        {
          id: "policy-attestations",
          label: "Policy Attestations Assigned to Me",
          icon: FileText,
          count: policyTasks.pendingAttestations.length,
          loading: policyTasksLoading,
          visible: policyTasks.pendingAttestations.length > 0,
        },
        {
          id: "scorecards-assigned",
          label: "Performance Reviews Assigned to Me",
          icon: Handshake,
          count: pendingScorecards.length,
          loading: scorecardsLoading,
          // Only shown to users who actually have review assignments — the vendor performance
          // workflow is invisible to everyone else.
          visible: scorecards.length > 0,
        },
        {
          id: "response-approvals",
          label: "AI Response Approvals",
          icon: ShieldOff,
          count: responseApprovals.length,
          loading: responseApprovalsLoading,
          // Only shown to the assigned approver — the response workflow is invisible to
          // everyone else (other admins reach pending requests from the asset panel).
          visible: responseApprovals.length > 0,
        },
        {
          id: "playbook-assignments",
          label: "AI Playbooks Assigned to Me",
          icon: BookOpen,
          count: playbookTasks.length,
          loading: playbookTasksLoading,
          // Only shown to assignees — the entry disappears when the playbook is saved.
          visible: playbookTasks.length > 0,
        },
        {
          id: "workflow-steps",
          label: "Workflow Steps Assigned to Me",
          icon: Workflow,
          count: workflowSteps.length,
          loading: workflowStepsLoading,
          // Feature-flagged (task 15): hidden entirely while Features:RemediationWorkflows is off.
          visible: remediationWorkflows,
        },
        {
          id: "pending-approvals",
          label: "Approvals",
          icon: Clock,
          count: pendingApprovals.length,
          loading: risksLoading,
          visible: true,
        },
      ].filter((item) => item.visible),
    },
    {
      key: "completed",
      heading: "Completed Tasks",
      items: [
        {
          id: "completed-assessments",
          label: "Assessments Assigned to Me",
          icon: ClipboardCheck,
          count: completedAssessmentsAssignedToMe.length,
          loading: loading,
          visible: true,
        },
        {
          id: "completed-risks",
          label: "Risks Assigned to Me",
          icon: CheckCircle,
          count: completedRisksAssignedToMe.length,
          loading: risksLoading,
          visible: true,
        },
        {
          id: "completed-evidence",
          label: "Evidence Requests Assigned to Me",
          icon: CheckCircle,
          count: completedEvidenceRequests.length,
          loading: evidenceLoading,
          visible: completedEvidenceRequests.length > 0,
        },
        {
          id: "completed-scorecards",
          label: "Performance Reviews Assigned to Me",
          icon: CheckCircle,
          count: completedScorecards.length,
          loading: scorecardsLoading,
          visible: completedScorecards.length > 0,
        },
      ].filter((item) => item.visible),
    },
  ];
  const navItems = sidebarGroups.flatMap((g) => g.items);

  // Render a completed assessment row — no Assess/Assign buttons (the item
  // is done), just a green "Completed" pill + a ghost View button that routes
  // to the same assessment-risks view the active cards use.
  const renderCompletedAssessmentCard = (assessment) => {
    const config = assessment._config;
    const TypeIcon = config.icon;
    const assessmentId = assessment[config.idField];
    return (
      <div
        key={`${assessment._type}-${assessmentId}`}
        className="p-3 border border-gray-200 rounded-lg transition-colors flex items-center justify-between"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="font-medium text-content-primary truncate">
            {getAssessmentTitle(assessment)}
          </span>
          <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 shrink-0 border ${config.color}`}>
            <TypeIcon className="h-3 w-3" />
            {config.label}
          </span>
        </div>
        <div className="ml-3 shrink-0 flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded-full">
            <CheckCircle className="h-3 w-3" />
            Completed
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate(config.risksRoute(assessmentId, assessment))}
          >
            <Eye className="h-3 w-3 mr-1" />
            View
          </Button>
        </div>
      </div>
    );
  };

  // Render a completed risk row — the pill text hinges on which predicate
  // fired. Accepted wins if both are true (stronger signal per the brief).
  const renderCompletedRiskCard = (risk) => {
    const config = risk._riskConfig;
    const TypeIcon = config.icon;
    const riskId = risk.id || risk.businessUnitRiskId;
    const accepted = (risk.status || "").toLowerCase() === "accepted";
    const pillLabel = accepted ? "Accepted" : "Remediated";
    return (
      <div
        key={`${risk._riskType}-${riskId}`}
        className="p-3 border border-gray-200 rounded-lg transition-colors flex items-center justify-between"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="font-medium text-content-primary truncate">
            {risk.riskTitle || "Untitled Risk"}
          </span>
          <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 shrink-0 border ${config.color}`}>
            <TypeIcon className="h-3 w-3" />
            {config.label}
          </span>
          {risk.riskCategory && (
            <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600 border border-gray-300 shrink-0">
              {risk.riskCategory}
            </span>
          )}
        </div>
        <div className="ml-3 shrink-0 flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded-full">
            <CheckCircle className="h-3 w-3" />
            {pillLabel}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate(`/risks?riskId=${riskId}`)}
          >
            <Eye className="h-3 w-3 mr-1" />
            View
          </Button>
        </div>
      </div>
    );
  };

  // Render a GRC evidence request card. The action routes to the GRC page's Evidence
  // Requests tab — for assignee-only users that page shows nothing but their requests.
  const renderEvidenceCard = (request) => {
    const statusCls = EVIDENCE_STATUS_STYLES[request.status] || EVIDENCE_STATUS_STYLES.Pending;
    const isSatisfied = request.status === "Satisfied";
    const dueLabel = request.dueDate
      ? new Date(request.dueDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
      : null;
    return (
      <div
        key={`evidence-${request.id}`}
        className="p-3 border-2 rounded-lg transition-colors flex items-center justify-between border-gray-200 hover:border-gray-300"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="font-medium text-content-primary truncate">
            {request.title}
          </span>
          <span className="px-2 py-1 text-xs rounded-full flex items-center gap-1 shrink-0 border bg-rose-100 text-rose-800 border-rose-300">
            <Scale className="h-3 w-3" />
            GRC Evidence
          </span>
          <span className={`px-2 py-1 text-xs rounded-full border shrink-0 ${statusCls}`}>
            {request.status}
          </span>
          {dueLabel && (
            <span className="text-xs text-gray-500 shrink-0">
              {isSatisfied ? "Valid until" : "Due"}: {dueLabel}
            </span>
          )}
        </div>
        <div className="ml-3 shrink-0">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate("/SecurityInsights", { state: { tab: "evidence-requests" } })}
          >
            {isSatisfied ? (
              <><Eye className="h-3 w-3 mr-1" />View</>
            ) : (
              <><FileText className="h-3 w-3 mr-1" />Upload Evidence</>
            )}
          </Button>
        </div>
      </div>
    );
  };

  // Render a vendor performance scorecard card. The action routes to the vendor dashboard —
  // for reviewer-only users that page collapses to the performance scorecard view.
  const renderScorecardCard = (scorecard) => {
    const statusCls = SCORECARD_STATUS_STYLES[scorecard.status] || SCORECARD_STATUS_STYLES.Pending;
    const isCompleted = scorecard.status === "Completed";
    const dueLabel = scorecard.dueDate
      ? new Date(scorecard.dueDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
      : null;
    return (
      <div
        key={`scorecard-${scorecard.id}`}
        className="p-3 border-2 rounded-lg transition-colors flex items-center justify-between border-gray-200 hover:border-gray-300"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="font-medium text-content-primary truncate">
            {scorecard.vendorName} Performance Review — {scorecard.reviewPeriod}
          </span>
          <span className="px-2 py-1 text-xs rounded-full flex items-center gap-1 shrink-0 border bg-orange-100 text-orange-800 border-orange-300">
            <Handshake className="h-3 w-3" />
            TPRM
          </span>
          <span className={`px-2 py-1 text-xs rounded-full border shrink-0 ${statusCls}`}>
            {scorecard.status}
          </span>
          {!isCompleted && dueLabel && (
            <span className="text-xs text-gray-500 shrink-0">Due: {dueLabel}</span>
          )}
        </div>
        <div className="ml-3 shrink-0">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate(`/VendorDashboard/${scorecard.vendorId}`)}
          >
            {isCompleted ? (
              <><Eye className="h-3 w-3 mr-1" />View</>
            ) : (
              <><ClipboardCheck className="h-3 w-3 mr-1" />Complete Review</>
            )}
          </Button>
        </div>
      </div>
    );
  };

  // Render a workflow step assigned to me (custom workflows, task 16). Open goes to the
  // Remediation Tracker deep link, which expands the remediation's Workflow section (task 13).
  const renderWorkflowStepCard = (step) => {
    const isOverdue = step.dueDate && new Date(step.dueDate) < new Date();
    // Same type pill as the risk cards; the backend sends the frontend bucket name (or null).
    const typeConfig = step.riskType ? RISK_TYPES[DTO_RISK_TYPE_TO_KEY[step.riskType] || "external"] : null;
    const StepTypeIcon = typeConfig?.icon;
    const dueLabel = step.dueDate
      ? new Date(step.dueDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
      : null;
    return (
      <div
        key={`workflow-step-${step.stepInstanceId}`}
        className="p-3 border-2 rounded-lg transition-colors flex items-center justify-between border-gray-200 hover:border-gray-300"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-medium text-content-primary truncate">
              {step.stepName}
            </span>
            <span className="px-2 py-1 text-xs rounded-full flex items-center gap-1 shrink-0 border bg-indigo-100 text-indigo-800 border-indigo-300">
              <Workflow className="h-3 w-3" />
              {step.templateName}
            </span>
            {typeConfig && (
              <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 shrink-0 border ${typeConfig.color}`}>
                <StepTypeIcon className="h-3 w-3" />
                {typeConfig.label}
              </span>
            )}
            {dueLabel && (
              <span className={`text-xs shrink-0 ${isOverdue ? "text-red-600 font-medium" : "text-gray-500"}`}>
                Due: {dueLabel}{isOverdue ? " (overdue)" : ""}
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-gray-500 truncate">
            {/* Both always labelled, even when the remediation repeats the risk's title (AX-238). */}
            <span>Remediation: </span>
            <span className="text-content-secondary">{step.remediationTitle}</span>
            {step.riskTitle ? <span> · Risk: {step.riskTitle}</span> : null}
          </div>
        </div>
        <div className="ml-3 shrink-0">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate(`/remediations?remediationId=${step.remediationId}`)}
          >
            <Eye className="h-3 w-3 mr-1" />
            Open
          </Button>
        </div>
      </div>
    );
  };

  // Centered empty-state block for any section.
  const renderEmpty = (Icon, title, body) => (
    <div className="py-10 flex flex-col items-center text-center">
      <div className="w-11 h-11 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center mb-3">
        <Icon className="h-4 w-4 text-gray-400" />
      </div>
      <p className="text-sm font-medium text-content-primary">{title}</p>
      <p className="text-xs text-gray-500 max-w-xs mt-1">{body}</p>
    </div>
  );

  // Section content renderer
  const renderSectionContent = () => {
    switch (activeSection) {
      case "assessments-assigned":
        return loading ? (
          <LoadingSpinner />
        ) : pendingAssessmentsAssignedToMe.length === 0 ? (
          renderEmpty(ClipboardCheck, "All caught up.", "New assessments assigned to you will appear here.")
        ) : (
          <div className="space-y-2">
            {pendingAssessmentsAssignedToMe.map((assessment) =>
              renderAssessmentCard(assessment, false)
            )}
          </div>
        );

      case "assessments-requiring":
        return loading ? (
          <LoadingSpinner />
        ) : assessmentsRequiringAssignment.length === 0 ? (
          renderEmpty(ClipboardList, "Nothing to assign.", "Unassigned assessments will appear here.")
        ) : (
          <div className="space-y-2">
            {assessmentsRequiringAssignment.map((assessment) =>
              renderAssessmentCard(assessment, true)
            )}
          </div>
        );

      case "risks-assigned":
        return risksLoading ? (
          <LoadingSpinner />
        ) : pendingRisksAssignedToMe.length === 0 ? (
          renderEmpty(AlertTriangle, "No risks assigned to you.", "Risks assigned to you will appear here until you accept or remediate them.")
        ) : (
          <div className="space-y-2">
            {pendingRisksAssignedToMe.map((risk) => renderRiskCard(risk, "view"))}
          </div>
        );

      case "risks-requiring":
        return risksLoading ? (
          <LoadingSpinner />
        ) : risksRequiringAssignment.length === 0 ? (
          renderEmpty(UserPlus, "Nothing to assign.", "Unassigned risks will appear here.")
        ) : (
          <div className="space-y-2">
            {risksRequiringAssignment.map((risk) => renderRiskCard(risk, "assign"))}
          </div>
        );

      case "completed-assessments":
        return loading ? (
          <LoadingSpinner />
        ) : completedAssessmentsAssignedToMe.length === 0 ? (
          renderEmpty(ClipboardCheck, "No completed assessments yet.", "Finish one and it'll show up here.")
        ) : (
          <div className="space-y-2">
            {completedAssessmentsAssignedToMe.map(renderCompletedAssessmentCard)}
          </div>
        );

      case "completed-risks":
        return risksLoading ? (
          <LoadingSpinner />
        ) : completedRisksAssignedToMe.length === 0 ? (
          renderEmpty(CheckCircle, "No completed risks yet.", "Accepted and remediated risks will appear here.")
        ) : (
          <div className="space-y-2">
            {completedRisksAssignedToMe.map(renderCompletedRiskCard)}
          </div>
        );

      case "evidence-assigned":
        return evidenceLoading ? (
          <LoadingSpinner />
        ) : pendingEvidenceRequests.length === 0 ? (
          renderEmpty(FileText, "No evidence requests due.", "GRC evidence requests assigned to you will appear here until you upload the requested evidence.")
        ) : (
          <div className="space-y-2">
            {pendingEvidenceRequests.map(renderEvidenceCard)}
          </div>
        );

      case "policy-reviews":
        return policyTasksLoading ? (
          <LoadingSpinner />
        ) : policyTasks.pendingReviews.length === 0 ? (
          renderEmpty(ClipboardCheck, "No policy reviews due.", "Policies you are assigned to review will appear here until you record a decision.")
        ) : (
          <div className="space-y-2">
            {policyTasks.pendingReviews.map((r) => (
              <div key={`policy-review-${r.policyId}`} className="p-3 border-2 rounded-lg transition-colors flex items-center justify-between border-gray-200 hover:border-gray-300">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="font-medium text-content-primary truncate">{r.title}</span>
                  <span className="px-2 py-1 text-xs rounded-full flex items-center gap-1 shrink-0 border bg-indigo-100 text-indigo-800 border-indigo-300">
                    <ClipboardCheck className="h-3 w-3" />
                    Policy review · v{r.version} round {r.reviewRound}
                  </span>
                  <span className="text-xs text-gray-500 shrink-0">Assigned {new Date(r.assignedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>
                </div>
                <div className="ml-3 shrink-0">
                  <Button size="sm" onClick={() => navigate("/SecurityInsights", { state: { tab: "policy", policyId: r.policyId, policyTab: "approvals" } })}>Open in Policy Life Cycle</Button>
                </div>
              </div>
            ))}
          </div>
        );

      case "policy-attestations":
        return policyTasksLoading ? (
          <LoadingSpinner />
        ) : policyTasks.pendingAttestations.length === 0 ? (
          renderEmpty(FileText, "No policy attestations due.", "Policy versions you are asked to read and attest to will appear here until you attest.")
        ) : (
          <div className="space-y-2">
            {policyTasks.pendingAttestations.map((a) => (
              <div key={`policy-attest-${a.assignmentId}`} className="p-3 border-2 rounded-lg transition-colors flex items-center justify-between border-gray-200 hover:border-gray-300">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="font-medium text-content-primary truncate">{a.title}</span>
                  <span className="px-2 py-1 text-xs rounded-full flex items-center gap-1 shrink-0 border bg-teal-100 text-teal-800 border-teal-300">
                    <FileText className="h-3 w-3" />
                    Attestation · v{a.version} · {a.stakeholderType}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded-full border shrink-0 ${a.status === "Overdue" ? "bg-red-100 text-red-800 border-red-300" : "bg-amber-100 text-amber-800 border-amber-300"}`}>{a.status}</span>
                  <span className="text-xs text-gray-500 shrink-0">Due: {new Date(a.dueDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>
                </div>
                <div className="ml-3 shrink-0">
                  <Button size="sm" onClick={() => navigate("/SecurityInsights", { state: { tab: "policy", policyId: a.policyId, policyTab: "attestations" } })}>Read &amp; attest</Button>
                </div>
              </div>
            ))}
          </div>
        );

      case "completed-evidence":
        return evidenceLoading ? (
          <LoadingSpinner />
        ) : completedEvidenceRequests.length === 0 ? (
          renderEmpty(CheckCircle, "No satisfied evidence requests yet.", "Evidence requests you've satisfied will appear here.")
        ) : (
          <div className="space-y-2">
            {completedEvidenceRequests.map(renderEvidenceCard)}
          </div>
        );

      case "scorecards-assigned":
        return scorecardsLoading ? (
          <LoadingSpinner />
        ) : pendingScorecards.length === 0 ? (
          renderEmpty(Handshake, "No performance reviews due.", "Vendor performance reviews assigned to you will appear here until you submit them.")
        ) : (
          <div className="space-y-2">
            {pendingScorecards.map(renderScorecardCard)}
          </div>
        );

      case "completed-scorecards":
        return scorecardsLoading ? (
          <LoadingSpinner />
        ) : completedScorecards.length === 0 ? (
          renderEmpty(CheckCircle, "No completed performance reviews yet.", "Vendor performance reviews you've submitted will appear here.")
        ) : (
          <div className="space-y-2">
            {completedScorecards.map(renderScorecardCard)}
          </div>
        );

      case "response-approvals":
        return responseApprovalsLoading ? (
          <LoadingSpinner />
        ) : responseApprovals.length === 0 ? (
          renderEmpty(ShieldOff, "No response actions awaiting your approval.", "AI response actions assigned to you as approver will appear here.")
        ) : (
          <div className="space-y-2">
            {responseApprovals.map((a) => (
              <div
                key={a.id}
                className="p-3 border-2 rounded-lg border-red-200 hover:border-red-300 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="font-medium text-content-primary truncate">
                    {RESPONSE_ACTION_LABEL[a.actionType] || a.actionType}
                    {" — "}{a.targetName}
                  </span>
                  <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 border border-red-300 shrink-0">
                    Awaiting Your Approval
                  </span>
                  <span className="text-xs text-content-secondary shrink-0">
                    Requested by {a.requestedByUserName || "unknown"}
                  </span>
                </div>
                <div className="ml-3 shrink-0">
                  <Button size="sm" variant="secondary"
                    onClick={() => navigate(`/AIIntelligence?assetId=${a.aiAssetId}`)}>
                    Review
                  </Button>
                </div>
              </div>
            ))}
          </div>
        );

      case "playbook-assignments":
        return playbookTasksLoading ? (
          <LoadingSpinner />
        ) : playbookTasks.length === 0 ? (
          renderEmpty(BookOpen, "No playbooks assigned to you.", "AI asset playbooks you've been asked to fill out will appear here until they are saved.")
        ) : (
          <div className="space-y-2">
            {playbookTasks.map((t) => (
              <div
                key={t.id}
                className="p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="font-medium text-content-primary truncate">
                    {t.assetName}
                  </span>
                  <span className="px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-800 border border-indigo-300 shrink-0">
                    Playbook to fill out
                  </span>
                  <span className="text-xs text-content-secondary shrink-0">
                    Assigned by {t.assignedByUserName || "unknown"}
                  </span>
                </div>
                <div className="ml-3 shrink-0">
                  <Button size="sm" variant="secondary"
                    onClick={() => navigate(`/AIIntelligence?assetId=${t.aiAssetId}`)}>
                    Open
                  </Button>
                </div>
              </div>
            ))}
          </div>
        );

      case "workflow-steps":
        return workflowStepsLoading ? (
          <LoadingSpinner />
        ) : workflowSteps.length === 0 ? (
          renderEmpty(Workflow, "No workflow steps assigned to you.", "Remediation workflow steps assigned to you will appear here until you complete them.")
        ) : (
          <div className="space-y-2">
            {workflowSteps.map(renderWorkflowStepCard)}
          </div>
        );

      case "pending-approvals":
        return risksLoading ? (
          <LoadingSpinner />
        ) : pendingApprovals.length === 0 ? (
          renderEmpty(Clock, "No pending approvals.", "Risks waiting for your review will appear here.")
        ) : (
          <div className="space-y-2">
            {pendingApprovals.map((r) => {
              const config = r._riskConfig;
              const TypeIcon = config.icon;
              const riskId = r.id || r.businessUnitRiskId;
              return (
                <div
                  key={`${r._riskType}-${riskId}`}
                  className="p-3 border-2 rounded-lg border-amber-200 hover:border-amber-300 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="font-medium text-content-primary truncate">
                      {r.riskTitle || "Untitled Risk"}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 shrink-0 border ${config.color}`}>
                      <TypeIcon className="h-3 w-3" />
                      {config.label}
                    </span>
                    <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800 border border-amber-300 shrink-0">
                      Pending Approval
                    </span>
                  </div>
                  <div className="ml-3 shrink-0">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setSelectedRisk({
                          ...r,
                          riskId: riskId,
                          _approveBaseEndpoint: config.approveBaseEndpoint(riskId),
                        });
                        setShowApprovalModal(true);
                      }}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Review
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        );

      default:
        return null;
    }
  };

  const activeSectionMeta = navItems.find((item) => item.id === activeSection);

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-content-primary mb-6">My Tasks</h1>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex gap-6 items-start">
          {/* Left Sidebar */}
          <div className="w-80 shrink-0">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-gray-200">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Sections
                </span>
              </div>
              <nav className="p-2">
                {sidebarGroups.map((group, idx) => (
                  <div key={group.key} className={idx > 0 ? "mt-3 pt-3 border-t border-gray-200" : ""}>
                    <div className="px-3 pb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-gray-400">
                      {group.heading}
                    </div>
                    <div className="space-y-1">
                      {group.items.map((item) => {
                        const Icon = item.icon;
                        const isActive = activeSection === item.id;
                        return (
                          <button
                            key={item.id}
                            onClick={() => setActiveSection(item.id)}
                            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                              isActive
                                ? "bg-indigo-50 text-indigo-700"
                                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                            }`}
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <Icon
                                className={`h-4 w-4 shrink-0 ${
                                  isActive ? "text-indigo-600" : "text-gray-400"
                                }`}
                              />
                              <span className="truncate">{item.label}</span>
                            </div>
                            <span
                              className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 tabular-nums ${
                                isActive
                                  ? "bg-indigo-100 text-indigo-700"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {item.loading ? "…" : item.count}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <Card>
              {activeSectionMeta && (
                <h2 className="text-lg font-semibold text-content-primary mb-4">
                  {activeSection.startsWith("completed-") ? "Completed · " : ""}{activeSectionMeta.label}
                </h2>
              )}
              {renderSectionContent()}
            </Card>
          </div>
        </div>
      </div>

      {/* Assign Assessment Modal */}
      {showAssignModal && selectedAssessment && (
        <OrgAssignAssessModal
          isOpen={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedAssessment(null);
          }}
          organizationId={selectedAssessment.organizationId}
          vendorId={selectedAssessment.vendorId}
          assessmentId={selectedAssessment[selectedAssessment._config.idField]}
          assessmentType={selectedAssessment._type}
          hasExistingAssessment={true}
          refreshData={fetchAllAssessments}
          dealId={selectedAssessment.dealID || selectedAssessment.dealId || undefined}
        />
      )}

      {/* Risk Acceptance Modal */}
      {showAcceptModal && selectedRisk && (
        <RiskAcceptanceModal
          show={showAcceptModal}
          onClose={() => {
            setShowAcceptModal(false);
            setSelectedRisk(null);
          }}
          risk={selectedRisk}
          acceptEndpoint={selectedRisk._acceptEndpoint}
          onAccepted={fetchAllRisks}
        />
      )}

      {/* Assign Risk Modal */}
      {showAssignRiskModal && selectedRisk && (
        <AssignRiskModal
          show={showAssignRiskModal}
          onClose={() => {
            setShowAssignRiskModal(false);
            setSelectedRisk(null);
          }}
          risk={selectedRisk}
          assignEndpoint={selectedRisk._assignEndpoint}
          refreshData={fetchAllRisks}
          dealId={selectedRisk.dealId || undefined}
        />
      )}

      {/* Risk Approval Modal */}
      {showApprovalModal && selectedRisk && (
        <RiskApprovalModal
          show={showApprovalModal}
          onClose={() => {
            setShowApprovalModal(false);
            setSelectedRisk(null);
          }}
          risk={selectedRisk}
          approveBaseEndpoint={selectedRisk._approveBaseEndpoint}
          refreshData={fetchAllRisks}
          riskId={selectedRisk.riskId}
          riskType={selectedRisk._riskConfig?.label}
        />
      )}
    </div>
  );
};

export default MyTasks;
